import { useNavigate } from "react-router-dom";
import { OnboardingProvider, useOnboarding } from "src/context/OnboardingContext";
import OnboardingLayout from "src/components/onboarding/OnboardingLayout";
import EducationLevel from "src/components/onboarding/student/EducationLevel";
import LearningGoals from "src/components/onboarding/student/LearningGoals";
import PersonalInfo from "src/components/onboarding/student/PersonalInfo";
import { ROUTES } from "src/routes/RouteConfig";
import { ProtectedRouteWrapper } from "src/components/auth/ProtectedRouteWrapper";
import useScrollToTop from "src/hooks/useScrollToTop";
import ProcessStepsModal from "src/components/ui/ProcessStepsModal";
import { useProcessStepsStore } from "src/store/processStepsStore";
import { useAuth } from "src/context/AuthContext";

// Wrapper component that uses the onboarding context
const OnboardingContent = () => {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  const {
    currentStep,
    totalSteps,
    nextStep,
    prevStep,
    completeOnboarding,
    educationLevel,
    setEducationLevel,
    learningGoals,
    setLearningGoals,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
  } = useOnboarding();

  // Get actions from the process steps store
  const { startProcess, updateStep, close } = useProcessStepsStore();

  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    if (currentStep === 1 && !educationLevel) return true;
    if (currentStep === 2 && learningGoals.length === 0) return true;
    if (currentStep === 3 && (!firstName || !lastName || !dateOfBirth))
      return true;
    return false;
  };

  // Handle the next button click
  const handleNext = () => {
    if (currentStep < totalSteps) {
      nextStep();
    } else {
      // Initialize the process steps modal with our steps
      startProcess("Onboarding Status", [
        { id: "saving", label: "Saving details", status: "pending" },
        { id: "onboarding", label: "Onboarding completed", status: "pending" },
        { id: "redirect", label: "Redirect to dashboard", status: "pending" },
      ]);

      // Update first step to loading
      updateStep("saving", "loading");

      // Call the completeOnboarding function from context
      completeOnboarding()
        .then(async () => {
          updateStep("saving", "complete");
          updateStep("onboarding", "complete");
          updateStep("redirect", "loading");

          // Longer delay to allow database trigger to complete and profile to be created
          setTimeout(async () => {
            // Refresh user data to get updated profile and onboarding status
            await refreshUserData();

            updateStep("redirect", "complete");
            // Navigate to dashboard after the process is complete
            navigate(ROUTES.STUDENT_DASHBOARD.path);
          }, 2000); // Increased delay to 2 seconds
        })
        .catch((error) => {
          console.error("Error during onboarding completion:", error);
          updateStep("saving", "error");
          // Close the modal after a short delay
          setTimeout(() => {
            close();
          }, 3000);
        });
    }
  };

  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <EducationLevel
            educationLevel={educationLevel}
            setEducationLevel={setEducationLevel}
          />
        );
      case 2:
        return (
          <LearningGoals
            learningGoals={learningGoals}
            setLearningGoals={setLearningGoals}
          />
        );
      case 3:
        return (
          <PersonalInfo
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            dateOfBirth={dateOfBirth}
            setDateOfBirth={setDateOfBirth}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <OnboardingLayout
        currentStep={currentStep}
        totalSteps={totalSteps}
        nextStep={handleNext}
        prevStep={prevStep}
        isNextDisabled={isNextDisabled()}
        nextButtonText={currentStep === totalSteps ? "Complete" : "Next"}
      >
        {renderStep()}
      </OnboardingLayout>

      {/* Process Steps Modal - no props needed as it uses the Zustand store */}
      <ProcessStepsModal />
    </>
  );
};

// Main component with provider
const StudentOnboarding = () => {
  useScrollToTop();

  return (
    <ProtectedRouteWrapper routeConfig={ROUTES.STUDENT_ONBOARDING}>
      <OnboardingProvider>
        <OnboardingContent />
      </OnboardingProvider>
    </ProtectedRouteWrapper>
  );
};

export default StudentOnboarding;

